// HINWEIS: Die Logik für Zuweisung & Führerscheinkontrolle lädt jetzt echte Daten aus Baserow.
// Die Komponente extrahiert Benutzerzuordnungen aus den Fahrzeugdaten in src/components/FahrerNutzerTable.tsx.
// Später können hier zusätzliche API-Methoden für erweiterte Benutzerverwaltung ergänzt werden.
import axios from 'axios';
import { BASEROW_CONFIG, getTableUrl } from '../config/baserowConfig';

// WICHTIG: Kein Token aus Build-/Runtime-Env im Client. Nur optional aus LocalStorage
// für lokale Entwicklung (bewusstes Opt-In). In Produktion setzt das BFF den Header.
const storageToken = typeof window !== 'undefined'
  ? (localStorage.getItem('REACT_APP_BASEROW_TOKEN') || localStorage.getItem('BASEROW_TOKEN'))
  : null;
const DATABASE_TOKEN = storageToken || undefined;

// API-Endpunkte
const VEHICLES_API_URL = getTableUrl(BASEROW_CONFIG.TABLES.VEHICLES);
const DAMAGE_REPORTS_API_URL = getTableUrl(BASEROW_CONFIG.TABLES.DAMAGE_REPORTS);
const DOCUMENTS_API_URL = getTableUrl(BASEROW_CONFIG.TABLES.DOCUMENTS);
const LICENSE_CHECKS_API_URL = getTableUrl(BASEROW_CONFIG.TABLES.LICENSE_CHECKS);
const USERS_API_URL = getTableUrl(BASEROW_CONFIG.TABLES.USERS);
const FILE_UPLOAD_URL = `${BASEROW_CONFIG.BASE_URL}/user-files/upload-file/`;

// Versteuerungs-Konfiguration
const TAXATION_OPTIONS = BASEROW_CONFIG.TAXATION_OPTIONS;

// Führerscheinkontrolle Feld-IDs
const LICENSE_CHECK_FIELDS = BASEROW_CONFIG.LICENSE_CHECK_FIELDS;

// Status-Optionen für Führerscheinkontrolle
const LICENSE_STATUS_OPTIONS = BASEROW_CONFIG.LICENSE_STATUS_OPTIONS;

// Hilfsfunktionen für Dateiverarbeitung
const getFileNameWithoutExtension = (filename: string): string => {
  return filename.replace(/\.[^/.]+$/, '');
};

const getMimeType = (filename: string): string => {
  const extension = filename.toLowerCase().split('.').pop();
  const mimeTypes: { [key: string]: string } = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  };
  return mimeTypes[extension || ''] || 'application/octet-stream';
};

class BaserowApi {
  private getHeaders() {
    // In Produktion wird der Token serverseitig gesetzt (BFF). Client setzt nur Content-Type.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (DATABASE_TOKEN) {
      headers['Authorization'] = `Token ${DATABASE_TOKEN}`; // nur Dev/Opt-In
    }
    return headers;
  }

  // Dateiupload-API-Methoden für Dokumentenablage
  async uploadFile(file: File): Promise<any> {
    try {
      console.log('=== DEBUG: uploadFile started ===');
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      console.log('Upload URL:', FILE_UPLOAD_URL);
      console.log('Token available:', !!DATABASE_TOKEN);

      const formData = new FormData();
      formData.append('file', file);
      
      console.log('=== DEBUG: FormData created ===');
      console.log('FormData contains file:', file.name);

      console.log('=== DEBUG: Making upload request ===');
      const response = await axios.post(FILE_UPLOAD_URL, formData, {
        headers: DATABASE_TOKEN ? { 'Authorization': `Token ${DATABASE_TOKEN}` } : undefined,
      });
      
      console.log('=== DEBUG: Upload response received ===');
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Upload response data:', response.data);
      
      // Return the response data with proper file URL
      const result = {
        ...response.data,
        url: response.data.url || response.data.file_url || response.data.download_url,
        name: response.data.name || response.data.file_name || file.name,
        size: response.data.size || response.data.file_size || file.size,
        uploaded_at: response.data.uploaded_at || new Date().toISOString()
      };
      
      console.log('=== DEBUG: Processed upload result ===');
      console.log('Final result:', result);
      
      return result;
    } catch (error: any) {
      console.error('=== DEBUG: uploadFile error ===');
      console.error('Error type:', typeof error);
      console.error('Error message:', error.message);
      console.error('Error response status:', error.response?.status);
      console.error('Error response data:', error.response?.data);
      console.error('Error response headers:', error.response?.headers);
      console.error('Full error object:', error);
      
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Hochladen der Datei:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Hochladen der Datei:', error);
      throw new Error('Fehler beim Hochladen der Datei');
    }
  }

  // Erweiterte downloadFile Methode für File-IDs
  async downloadFileById(fileId: number): Promise<Blob> {
    try {
      const response = await axios.get(`${BASEROW_CONFIG.BASE_URL}/user-files/upload-file/${fileId}/`, {
        headers: DATABASE_TOKEN ? { 'Authorization': `Token ${DATABASE_TOKEN}` } : undefined,
        responseType: 'blob'
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Laden der Datei:', error);
      throw new Error('Fehler beim Laden der Datei');
    }
  }

  async downloadFile(fileName: string): Promise<ArrayBuffer> {
    try {
      // Versuche die Datei direkt über den Baserow-File-Endpunkt herunterzuladen
      // Baserow verwendet normalerweise einen anderen Endpunkt für Downloads
      const response = await axios.get(`${FILE_UPLOAD_URL}${fileName}/`, {
        headers: DATABASE_TOKEN ? { 'Authorization': `Token ${DATABASE_TOKEN}` } : undefined,
        responseType: 'arraybuffer',
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Herunterladen der Datei:', error);
      throw new Error('Fehler beim Herunterladen der Datei');
    }
  }

  async deleteFile(fileName: string): Promise<void> {
    try {
      // Versuche die Datei direkt über den Baserow-File-Endpunkt zu löschen
      await axios.delete(`${FILE_UPLOAD_URL}${fileName}/`, {
        headers: DATABASE_TOKEN ? { 'Authorization': `Token ${DATABASE_TOKEN}` } : undefined,
      });
    } catch (error: any) {
      console.error('Fehler beim Löschen der Datei:', error);
      throw new Error('Fehler beim Löschen der Datei');
    }
  }

  // Dokument-API-Methoden für die Dokumentenablage
  // Dokument in der documents Tabelle erstellen (Tabelle 568)
  async createDocument(data: any): Promise<any> {
    try {
      console.log('=== DEBUG: createDocument started ===');
      console.log('Input data:', data);
      
      // Für Führerschein-Dokumente ist kein carpool erforderlich
      if (!data.carpool || data.carpool.length === 0) {
        if (!data.isLicenseDocument) {
          console.error('No carpool data provided');
          throw new Error('Fahrzeug-ID ist erforderlich');
        }
      }
      
      // Dokument in der documents Tabelle erstellen - genau wie Python-Code
      const documentData: any = {
        title: data.title || 'Unbenanntes Dokument',
        description: data.description || '',
        files: data.files || [], // Direkt als Array von Dateinamen
        active: data.active !== undefined ? data.active : true,
        content_type: data.content_type || 'application/pdf',
        type: data.type || 2688
      };

      // Nur für normale Dokumente (nicht Führerschein-Dokumente) carpool hinzufügen
      let vehicleId = null;
      if (data.carpool && data.carpool.length > 0) {
        vehicleId = data.carpool[0];
        console.log('Vehicle ID:', vehicleId);
        documentData.carpool = [vehicleId];
      } else if (data.isLicenseDocument) {
        console.log('Creating license document without carpool link');
        // Führerschein-Dokumente haben kein carpool-Link
      }
      
      console.log('=== DEBUG: Files to link ===');
      console.log('Files data:', data.files);
      console.log('Files in documentData:', documentData.files);
      
      console.log('=== DEBUG: Creating document ===');
      console.log('Document data to send:', documentData);
      console.log('API URL:', `${DOCUMENTS_API_URL}?user_field_names=true`);
      console.log('Headers:', this.getHeaders());
      
      const response = await axios.post(`${DOCUMENTS_API_URL}?user_field_names=true`, documentData, {
        headers: this.getHeaders(),
      });
      
      console.log('=== DEBUG: Document created ===');
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      // Rückgabe des erstellten Dokuments
      const result: any = {
        id: response.data.id,
        title: response.data.title || data.title,
        description: response.data.description || data.description,
        active: response.data.active !== undefined ? response.data.active : true,
        type: response.data.type || data.type,
        content_type: response.data.content_type || data.content_type,
        files: response.data.files || data.files || [],
        created_at: response.data.created_at,
        updated_at: response.data.updated_at
      };
      
      // Nur für normale Dokumente carpool hinzufügen
      if (vehicleId) {
        result.carpool = [{ id: vehicleId }];
      }
      
      return result;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Anlegen des Dokuments:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Anlegen des Dokuments:', error);
      throw new Error('Fehler beim Anlegen des Dokuments');
    }
  }

  async updateDocument(id: number, data: any): Promise<any> {
    try {
      const response = await axios.patch(`${DOCUMENTS_API_URL}${id}/?user_field_names=true`, data, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Aktualisieren des Dokuments:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Aktualisieren des Dokuments:', error);
      throw new Error('Fehler beim Aktualisieren des Dokuments');
    }
  }



  async deleteDocument(id: number): Promise<void> {
    console.log('Archiviere Dokument (Soft-Delete):', id);
    try {
      // Verwende direkt Soft-Delete (active = false) da Update-Berechtigung vorhanden
      const response = await axios.patch(`${DOCUMENTS_API_URL}${id}/?user_field_names=true`, { active: false }, {
        headers: this.getHeaders(),
      });
      console.log('Dokument erfolgreich archiviert (Soft-Delete):', response.status);
    } catch (error: any) {
      console.error('Soft-Delete fehlgeschlagen:', error?.response?.status, error?.response?.data);
      
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      const detail = error?.response?.data?.detail;
      
      console.log('Fehlerdetails:', { status, code, detail });
      
      // Spezifische Fehlermeldungen für besseres Debugging
      if (status === 401 || status === 403) {
        throw new Error('Keine Berechtigung zum Archivieren des Dokuments. Bitte prüfen Sie die Token-Berechtigungen.');
      } else if (status === 404) {
        throw new Error('Dokument nicht gefunden. Es wurde möglicherweise bereits gelöscht.');
      } else {
        throw new Error(`Fehler beim Archivieren des Dokuments: ${detail || error.message}`);
      }
    }
  }

  // Fahrzeug-API-Methoden (zurück zu ursprünglicher Implementierung)
  async getVehicles(): Promise<any> {
    try {
      const response = await axios.get(`${VEHICLES_API_URL}?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      // Konvertiere Versteuerungs-IDs zu Strings und setze Standard-Versteuerung
      if (response.data.results) {
        response.data.results = response.data.results.map((vehicle: any) => {
          // Wenn taxation in der API vorhanden ist, verwende den echten Wert
          let taxation = vehicle.taxation ? this.getTaxationValue(vehicle.taxation) : null;
          
          // Nur wenn taxation wirklich null/undefined ist UND es kein Poolfahrzeug ist, setze Standard
          if (!taxation && !vehicle.poolcar) {
            taxation = '1% Verbrenner';
          } else if (!taxation && vehicle.poolcar) {
            taxation = 'Keine Versteuerung';
          }
          
          return {
            ...vehicle,
            taxation: taxation
          };
        });
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden der Fahrzeuge:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden der Fahrzeuge:', error);
      throw new Error('Fehler beim Laden der Fahrzeuge');
    }
  }

  async getVehicle(id: number): Promise<any> {
    try {
      const response = await axios.get(`${VEHICLES_API_URL}${id}/?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      // Konvertiere Versteuerungs-ID zu String und setze Standard-Versteuerung
      if (response.data) {
        let taxation = response.data.taxation ? this.getTaxationValue(response.data.taxation) : 'Keine Versteuerung';
        
        // Für alle Fahrzeuge außer Poolfahrzeugen: Standard "1% Verbrenner"
        if (!response.data.poolcar && (taxation === 'Keine Versteuerung' || !taxation)) {
          taxation = '1% Verbrenner';
        }
        
        response.data.taxation = taxation;
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden des Fahrzeugs:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden des Fahrzeugs:', error);
      throw new Error('Fehler beim Laden des Fahrzeugs');
    }
  }

  async createVehicle(data: any): Promise<any> {
    try {
      // Bereinige und validiere die Daten vor dem Senden
      const cleanedData: any = {};
      
      // Nur definierte und nicht-leere Werte senden
      if (data.model !== undefined && data.model !== '') cleanedData.model = data.model;
      if (data.manufacturer !== undefined && data.manufacturer !== '') cleanedData.manufacturer = data.manufacturer;
      if (data.type !== undefined && data.type !== '') {
        // Für Baserow-Select-Felder müssen wir die entsprechende ID senden
        // Hier müssten wir die ID für den Fahrzeugtyp finden
        // Für jetzt senden wir den Wert als String
        cleanedData.type = data.type;
      }
      if (data.type_of_contract !== undefined && data.type_of_contract !== '') cleanedData.type_of_contract = data.type_of_contract;
      if (data.license_plate !== undefined && data.license_plate !== '') cleanedData.license_plate = data.license_plate;
      if (data.vin !== undefined) cleanedData.vin = data.vin;
      if (data.tuev_until !== undefined && data.tuev_until !== '') cleanedData.tuev_until = data.tuev_until;
      if (data.km !== undefined && data.km !== '') cleanedData.km = data.km;
      if (data.end_of_contract !== undefined && data.end_of_contract !== '') cleanedData.end_of_contract = data.end_of_contract;
      if (data.contract_end_date !== undefined && data.contract_end_date !== '') cleanedData.contract_end_date = data.contract_end_date;
      if (data.monthly_net_sum !== undefined && data.monthly_net_sum !== '') cleanedData.monthly_net_sum = data.monthly_net_sum;
      if (data.monthly_gross_sum !== undefined && data.monthly_gross_sum !== '') cleanedData.monthly_gross_sum = data.monthly_gross_sum;
      if (data.poolcar !== undefined) cleanedData.poolcar = data.poolcar;
      if (data.date_of_last_km_check !== undefined && data.date_of_last_km_check !== '') cleanedData.date_of_last_km_check = data.date_of_last_km_check;
      
      // Versteuerungs-Logik
      if (data.taxation !== undefined) {
        // Konvertiere String-Werte zu Baserow-IDs
        let taxationId = TAXATION_OPTIONS.NONE; // Standard: Keine Versteuerung
        
        if (data.taxation === '1% Verbrenner') {
          taxationId = TAXATION_OPTIONS.COMBUSTION;
        } else if (data.taxation === '0,5% Hybrid') {
          taxationId = TAXATION_OPTIONS.HYBRID;
        } else if (data.taxation === '0,25% Elektro') {
          taxationId = TAXATION_OPTIONS.ELECTRIC;
        }
        
        cleanedData.taxation = taxationId;
      }
      
      console.log('Sending cleaned data to Baserow:', cleanedData);
      
      const response = await axios.post(`${VEHICLES_API_URL}?user_field_names=true`, cleanedData, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Anlegen des Fahrzeugs:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Anlegen des Fahrzeugs:', error);
      throw new Error('Fehler beim Anlegen des Fahrzeugs');
    }
  }

  async updateVehicle(id: number, data: any): Promise<any> {
    try {
      // Bereinige und validiere die Daten vor dem Senden
      const cleanedData: any = {};
      
      // Nur definierte und nicht-leere Werte senden
      if (data.model !== undefined && data.model !== '') cleanedData.model = data.model;
      if (data.manufacturer !== undefined && data.manufacturer !== '') cleanedData.manufacturer = data.manufacturer;
      if (data.type !== undefined && data.type !== '') {
        // Für Baserow-Select-Felder müssen wir die entsprechende ID senden
        // Hier müssten wir die ID für den Fahrzeugtyp finden
        // Für jetzt senden wir den Wert als String
        cleanedData.type = data.type;
      }
      if (data.type_of_car !== undefined && data.type_of_car !== '') {
        // Das type_of_car Feld wird auf das type Feld in Baserow gemappt
        cleanedData.type = data.type_of_car;
      }
      if (data.type_of_contract !== undefined && data.type_of_contract !== '') cleanedData.type_of_contract = data.type_of_contract;
      if (data.license_plate !== undefined && data.license_plate !== '') cleanedData.license_plate = data.license_plate;
      if (data.vin !== undefined) cleanedData.vin = data.vin;
      if (data.tuev_until !== undefined && data.tuev_until !== '') cleanedData.tuev_until = data.tuev_until;
      if (data.km !== undefined && data.km !== '') cleanedData.km = data.km;
      if (data.end_of_contract !== undefined && data.end_of_contract !== '') cleanedData.end_of_contract = data.end_of_contract;
      if (data.contract_end_date !== undefined && data.contract_end_date !== '') cleanedData.contract_end_date = data.contract_end_date;
      if (data.monthly_net_sum !== undefined && data.monthly_net_sum !== '') cleanedData.monthly_net_sum = data.monthly_net_sum;
      if (data.monthly_gross_sum !== undefined && data.monthly_gross_sum !== '') cleanedData.monthly_gross_sum = data.monthly_gross_sum;
      if (data.poolcar !== undefined) cleanedData.poolcar = data.poolcar;
      if (data.date_of_last_km_check !== undefined && data.date_of_last_km_check !== '') cleanedData.date_of_last_km_check = data.date_of_last_km_check;
      
      // Versteuerungs-Logik
      if (data.taxation !== undefined) {
        // Konvertiere String-Werte zu Baserow-IDs
        let taxationId = TAXATION_OPTIONS.NONE; // Standard: Keine Versteuerung
        
        if (data.taxation === '1% Verbrenner') {
          taxationId = TAXATION_OPTIONS.COMBUSTION;
        } else if (data.taxation === '0,5% Hybrid') {
          taxationId = TAXATION_OPTIONS.HYBRID;
        } else if (data.taxation === '0,25% Elektro') {
          taxationId = TAXATION_OPTIONS.ELECTRIC;
        }
        
        cleanedData.taxation = taxationId;
      }
      
      console.log('Sending cleaned data to Baserow:', cleanedData);
      
      const response = await axios.patch(`${VEHICLES_API_URL}${id}/?user_field_names=true`, cleanedData, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Aktualisieren des Fahrzeugs:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Aktualisieren des Fahrzeugs:', error);
      throw new Error('Fehler beim Aktualisieren des Fahrzeugs');
    }
  }

  async deleteVehicle(id: number): Promise<void> {
    try {
      await axios.delete(`${VEHICLES_API_URL}${id}/`, {
        headers: this.getHeaders(),
      });
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Löschen des Fahrzeugs:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Löschen des Fahrzeugs:', error);
      throw new Error('Fehler beim Löschen des Fahrzeugs');
    }
  }

  // Dokumente aus der documents Tabelle laden (Tabelle 568) mit carpool View (4958)
  async getDocuments(): Promise<any> {
    try {
      console.log('=== DEBUG: Loading documents from table 568 with carpool view 4958 ===');
      const apiUrl = `${DOCUMENTS_API_URL}?user_field_names=true&view_id=4958`;
      console.log('API URL:', apiUrl);
      console.log('Headers:', this.getHeaders());
      
      // Lade alle Dokumente mit Paginierung
      let allDocuments: any[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const paginatedUrl = `${apiUrl}&page=${page}&size=100`;
        console.log(`Loading page ${page}:`, paginatedUrl);
        
        const response = await axios.get(paginatedUrl, {
          headers: this.getHeaders(),
        });
        
        console.log(`=== DEBUG: Page ${page} response ===`);
        console.log('Response data:', response.data);
        console.log('Number of results on this page:', response.data.results?.length || 0);
        
        if (response.data.results && response.data.results.length > 0) {
          allDocuments = allDocuments.concat(response.data.results);
          page++;
        } else {
          hasMore = false;
        }
        
        // Prüfe auch ob es eine next URL gibt
        if (!response.data.next) {
          hasMore = false;
        }
      }
      
      console.log('=== DEBUG: All documents loaded ===');
      console.log('Total number of documents:', allDocuments.length);
      
      // Transformiere die Dokumente entsprechend der neuen Baserow-Struktur
      // Die carpool View (4958) filtert bereits die richtigen Dokumente
      // Filtere archivierte Dokumente (active = false) heraus
      const activeDocuments = allDocuments.filter(doc => doc.active !== false);
      console.log('Active documents after filtering:', activeDocuments.length, 'of', allDocuments.length);
      
      const documents = activeDocuments.map((doc: any) => {
          console.log('Processing document:', doc);
          
          // Extrahiere die richtigen Felder aus der Baserow-Antwort
          // Hole die aktuelle Download-URL aus dem files Array
          let downloadUrl = null;
          if (doc.files && Array.isArray(doc.files) && doc.files.length > 0) {
            downloadUrl = doc.files[0].url || doc.files[0].download_url;
            console.log('Using download URL from files array:', downloadUrl);
          } else if (doc.file_url) {
            downloadUrl = doc.file_url;
            console.log('Using fallback file_url:', downloadUrl);
          }
          
          const transformedDoc = {
            id: doc.id,
            display: doc.display || doc.name || `Dokument ${doc.id}`,
            description: doc.description || '',
            active: doc.active !== undefined ? doc.active : true,
            title: doc.title || doc.name || `Dokument ${doc.id}`,
            uuid: doc.uuid || '',
            file_url: downloadUrl, // Verwende die aktuelle Download-URL
            type: doc.type?.value || doc.type || 'other',
            files: doc.files || [],
            created_at: doc.created_at,
            updated_at: doc.updated_at,
            carpool: doc.carpool || [],
            carpool_license_check: doc.carpool_license_check || [],
            content_type: doc.content_type || '',
            // Weitere Felder können bei Bedarf hinzugefügt werden
          };
          
          console.log('Transformed document:', transformedDoc);
          return transformedDoc;
        });
      
      console.log('=== DEBUG: Final transformed documents ===');
      console.log('Total documents after transformation:', documents.length);
      if (documents.length > 0) {
        console.log('First document:', documents[0]);
      }
      
      return {
        results: documents
      };
    } catch (error: any) {
      console.error('=== DEBUG: API Error Details ===');
      console.error('Error message:', error.message);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden der Dokumente:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden der Dokumente:', error);
      throw new Error('Fehler beim Laden der Dokumente');
    }
  }

  // Neue Methode: Dokumente für ein spezifisches Fahrzeug laden
  async getDocumentsForVehicle(vehicleId: number): Promise<any> {
    try {
      console.log(`=== DEBUG: Loading documents for vehicle ${vehicleId} using carpool view ===`);
      
      // Verwende die carpool View (4958) mit Paginierung wie in getDocuments
      const apiUrl = `${DOCUMENTS_API_URL}?user_field_names=true&view_id=4958`;
      console.log('API URL:', apiUrl);
      
      // Lade alle Dokumente mit Paginierung
      let allDocuments: any[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const paginatedUrl = `${apiUrl}&page=${page}&size=100`;
        console.log(`Loading page ${page}:`, paginatedUrl);
        
        const response = await axios.get(paginatedUrl, {
          headers: this.getHeaders(),
        });
        
        console.log(`=== DEBUG: Page ${page} response ===`);
        console.log('Number of results on this page:', response.data.results?.length || 0);
        
        if (response.data.results && response.data.results.length > 0) {
          allDocuments = allDocuments.concat(response.data.results);
          page++;
        } else {
          hasMore = false;
        }
        
        // Prüfe auch ob es eine next URL gibt
        if (!response.data.next) {
          hasMore = false;
        }
      }
      
      console.log(`=== DEBUG: Loaded ${allDocuments.length} total documents, filtering for vehicle ${vehicleId} ===`);
      
      // Filtere Dokumente, die dem spezifischen Fahrzeug zugeordnet sind und aktiv sind
      const vehicleDocuments = allDocuments.filter((doc: any) => {
        const carpoolIds = doc.carpool?.map((car: any) => car.id) || [];
        const hasVehicle = carpoolIds.includes(vehicleId);
        const isActive = doc.active !== false;
        
        console.log(`Document ${doc.id}: carpool=${JSON.stringify(doc.carpool)}, hasVehicle=${hasVehicle}, active=${doc.active}, isActive=${isActive}`);
        
        return hasVehicle && isActive;
      });
      
      console.log(`=== DEBUG: Found ${vehicleDocuments.length} documents for vehicle ${vehicleId} ===`);
      
      // Transformiere die Dokumente (gleiche Logik wie in getDocuments)
      const documents = vehicleDocuments.map((doc: any) => {
        console.log('Processing vehicle document:', doc);
        
        // Hole die aktuelle Download-URL aus dem files Array
        let downloadUrl = null;
        if (doc.files && Array.isArray(doc.files) && doc.files.length > 0) {
          downloadUrl = doc.files[0].url || doc.files[0].download_url;
          console.log('Using download URL from files array:', downloadUrl);
        } else if (doc.file_url) {
          downloadUrl = doc.file_url;
          console.log('Using fallback file_url:', downloadUrl);
        }
        
        const transformedDoc = {
          id: doc.id,
          display: doc.display || doc.name || `Dokument ${doc.id}`,
          description: doc.description || '',
          active: doc.active !== undefined ? doc.active : true,
          title: doc.title || doc.name || `Dokument ${doc.id}`,
          uuid: doc.uuid || '',
          file_url: downloadUrl, // Verwende die aktuelle Download-URL
          type: doc.type?.value || doc.type || 'other',
          files: doc.files || [],
          created_at: doc.created_at,
          updated_at: doc.updated_at,
          carpool: doc.carpool || [],
          carpool_license_check: doc.carpool_license_check || [],
          content_type: doc.content_type || '',
        };
        
        console.log('Transformed vehicle document:', transformedDoc);
        return transformedDoc;
      });
      
      return {
        results: documents
      };
    } catch (error: any) {
      console.error('Fehler beim Laden der Fahrzeug-Dokumente:', error);
      throw new Error('Fehler beim Laden der Fahrzeug-Dokumente');
    }
  }

  // Spezielle Debug-Funktion für die documents Tabelle
  async debugDocumentsTable(): Promise<any> {
    try {
      console.log('=== DEBUG: Testing documents table connection ===');
      console.log('Table ID: 568');
      console.log('API URL:', DOCUMENTS_API_URL);
      console.log('Token:', DATABASE_TOKEN ? 'Token vorhanden' : 'Kein Token');
      
      // Teste die Verbindung zur documents Tabelle
      const response = await axios.get(`${DOCUMENTS_API_URL}?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      console.log('=== DEBUG: Documents table test response ===');
      console.log('Status:', response.status);
      console.log('Data:', response.data);
      console.log('Results count:', response.data.results?.length || 0);
      
      // Zeige alle Dokumente ohne Filter
      if (response.data.results && response.data.results.length > 0) {
        console.log('=== DEBUG: All documents in table ===');
        response.data.results.forEach((doc: any, index: number) => {
          console.log(`Document ${index + 1}:`, {
            id: doc.id,
            name: doc.name,
            file: doc.file,
            type: doc.type,
            carpool: doc.carpool
          });
        });
      } else {
        console.log('=== DEBUG: No documents found in table ===');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('=== DEBUG: Documents table test failed ===');
      console.error('Error:', error.message);
      console.error('Response:', error.response?.data);
      throw error;
    }
  }

  // Funktion zum Abrufen der Tabellenstruktur
  async getTableStructure(): Promise<any> {
    try {
      console.log('=== DEBUG: Getting table structure ===');
      
      // Hole die Tabellenstruktur von Baserow
      const tableUrl = `${BASEROW_CONFIG.BASE_URL}/database/tables/568/`;
      const response = await axios.get(tableUrl, {
        headers: this.getHeaders(),
      });
      
      console.log('=== DEBUG: Table structure ===');
      console.log('Table info:', response.data);
      
      // Hole auch die Feldinformationen
      const fieldsUrl = `${BASEROW_CONFIG.BASE_URL}/database/fields/table/568/`;
      const fieldsResponse = await axios.get(fieldsUrl, {
        headers: this.getHeaders(),
      });
      
      console.log('=== DEBUG: Table fields ===');
      console.log('Fields:', fieldsResponse.data);
      
      return {
        table: response.data,
        fields: fieldsResponse.data
      };
    } catch (error: any) {
      console.error('=== DEBUG: Failed to get table structure ===');
      console.error('Error:', error.message);
      console.error('Response:', error.response?.data);
      throw error;
    }
  }

  // Neue Download-URL für eine Datei generieren
  async getFileDownloadUrl(fileName: string): Promise<string | null> {
    try {
      console.log('=== DEBUG: Getting fresh download URL for file ===');
      console.log('File name:', fileName);
      
      // Baserow stellt eine API bereit, um frische Download-URLs zu generieren
      const url = `${BASEROW_CONFIG.BASE_URL}/user-files/${fileName}/`;
      console.log('Download URL request:', url);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(),
      });
      
      console.log('Fresh download URL response:', response.data);
      return response.data.url || response.data.download_url || null;
    } catch (error: any) {
      console.error('Fehler beim Generieren der Download-URL:', error);
      return null;
    }
  }

  // Fahrzeugbilder hochladen und mit Fahrzeug verknüpfen
  async uploadVehicleImages(vehicleId: number, imageFiles: File[]): Promise<any> {
    try {
      console.log('=== DEBUG: Uploading vehicle images ===');
      console.log('Vehicle ID:', vehicleId);
      console.log('Number of images:', imageFiles.length);
      
      // 1. Alle Bilder zu Baserow hochladen
      const uploadPromises = imageFiles.map(async (file) => {
        console.log('Uploading image:', file.name);
        return await this.uploadFile(file);
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      console.log('All images uploaded:', uploadResults);
      
      // 2. Fahrzeug mit neuen Bildern aktualisieren
      const imageNames = uploadResults.map(result => result.name);
      console.log('Image names to save:', imageNames);
      
      // Hole aktuelles Fahrzeug um bestehende Bilder zu erhalten
      const currentVehicle = await this.getVehicle(vehicleId);
      const existingImages = currentVehicle.images || [];
      const existingImageNames = existingImages.map((img: any) => img.name || img);
      
      // Kombiniere bestehende und neue Bilder
      const allImageNames = [...existingImageNames, ...imageNames];
      console.log('All image names (existing + new):', allImageNames);
      
      const updateData = {
        images: allImageNames
      };
      
      console.log('Updating vehicle with images:', updateData);
      const updatedVehicle = await this.updateVehicle(vehicleId, updateData);
      
      console.log('Vehicle updated with images:', updatedVehicle);
      return {
        uploadResults,
        updatedVehicle
      };
    } catch (error: any) {
      console.error('Fehler beim Hochladen der Fahrzeugbilder:', error);
      throw new Error('Fehler beim Hochladen der Fahrzeugbilder');
    }
  }

  // Fahrzeugbild löschen
  async deleteVehicleImage(vehicleId: number, imageName: string): Promise<any> {
    try {
      console.log('=== DEBUG: Deleting vehicle image ===');
      console.log('Vehicle ID:', vehicleId);
      console.log('Image name:', imageName);
      
      // Hole aktuelles Fahrzeug
      const currentVehicle = await this.getVehicle(vehicleId);
      const currentImages = currentVehicle.images || [];
      const currentImageNames = currentImages.map((img: any) => img.name || img);
      
      // Entferne das spezifische Bild
      const updatedImageNames = currentImageNames.filter((name: string) => name !== imageName);
      console.log('Updated image names:', updatedImageNames);
      
      const updateData = {
        images: updatedImageNames
      };
      
      console.log('Updating vehicle after image deletion:', updateData);
      const updatedVehicle = await this.updateVehicle(vehicleId, updateData);
      
      console.log('Vehicle updated after image deletion:', updatedVehicle);
      return updatedVehicle;
    } catch (error: any) {
      console.error('Fehler beim Löschen des Fahrzeugbildes:', error);
      throw new Error('Fehler beim Löschen des Fahrzeugbildes');
    }
  }

  // Einzelnes Dokument aus der documents Tabelle laden
  async getDocument(id: number): Promise<any> {
    try {
      const response = await axios.get(`${DOCUMENTS_API_URL}${id}/?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      const doc = response.data;
      console.log('Raw document data:', doc);
      
      // Hole die aktuelle Download-URL aus dem files Array
      let downloadUrl = null;
      let files = [];
      
      if (doc.files && Array.isArray(doc.files) && doc.files.length > 0) {
        files = doc.files.map((file: any) => ({
          url: file.url || file.download_url,
          name: file.name
        }));
        downloadUrl = doc.files[0].url || doc.files[0].download_url;
        console.log('Using download URL from files array:', downloadUrl);
      } else if (doc.file_url) {
        downloadUrl = doc.file_url;
        files = [{ url: downloadUrl, name: doc.title || doc.name }];
        console.log('Using file_url:', downloadUrl);
      }
      
      console.log('Final download URL:', downloadUrl);
      console.log('Files array:', files);
      
      return {
        id: doc.id,
        title: doc.title || doc.name,
        files: files,
        file_url: downloadUrl,
        content_type: doc.content_type,
        created_at: doc.created_at,
        updated_at: doc.updated_at,
        carpool: doc.carpool || []
      };
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden des Dokuments:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden des Dokuments:', error);
      throw new Error('Fehler beim Laden des Dokuments');
    }
  }

  // Schadensmanagement-API-Methoden (bleiben unverändert)
  async getDamageReports(): Promise<any> {
    try {
      const response = await axios.get(`${DAMAGE_REPORTS_API_URL}?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden der Schadensmeldungen:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden der Schadensmeldungen:', error);
      throw new Error('Fehler beim Laden der Schadensmeldungen');
    }
  }

  async getDamageReport(id: number): Promise<any> {
    try {
      const response = await axios.get(`${DAMAGE_REPORTS_API_URL}${id}/?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Laden der Schadensmeldung:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Laden der Schadensmeldung:', error);
      throw new Error('Fehler beim Laden der Schadensmeldung');
    }
  }

  async createDamageReport(data: any): Promise<any> {
    try {
      // Konvertiere die Daten in das Baserow-Format gemäß API-Dokumentation
      const baserowData: any = {
        title: data.title,
        description: data.description,
        status: data.status, // Status-ID (5382=Gemeldet, 5383=in Bearbeitung, 5384=erledigt)
        estimated_costs: data.estimated_costs || "0.00",
        real_costs: data.real_costs || "0.00"
      };

      // Nur carpool hinzufügen, wenn es nicht leer ist und gültige Werte enthält
      if (data.carpool && Array.isArray(data.carpool) && data.carpool.length > 0) {
        // Filtere leere oder ungültige Werte heraus
        const validCarpool = data.carpool.filter((id: any) => id && id !== null && id !== undefined);
        if (validCarpool.length > 0) {
          baserowData.carpool = validCarpool;
        }
      }

      // Nur documents hinzufügen, wenn es nicht leer ist und gültige Werte enthält
      if (data.documents && Array.isArray(data.documents) && data.documents.length > 0) {
        // Filtere leere oder ungültige Werte heraus
        const validDocuments = data.documents.filter((id: any) => id && id !== null && id !== undefined);
        if (validDocuments.length > 0) {
          baserowData.documents = validDocuments;
        }
      }

      console.log('Sending damage report data:', baserowData);

      const response = await axios.post(`${DAMAGE_REPORTS_API_URL}?user_field_names=true`, baserowData, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Anlegen der Schadensmeldung:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Anlegen der Schadensmeldung:', error);
      throw new Error('Fehler beim Anlegen der Schadensmeldung');
    }
  }

  async updateDamageReport(id: number, data: any): Promise<any> {
    try {
      // Konvertiere die Daten in das Baserow-Format gemäß API-Dokumentation
      const baserowData: any = {};
      
      if (data.title !== undefined) baserowData.title = data.title;
      if (data.description !== undefined) baserowData.description = data.description;
      if (data.status !== undefined) baserowData.status = data.status;
      if (data.estimated_costs !== undefined) baserowData.estimated_costs = data.estimated_costs;
      if (data.real_costs !== undefined) baserowData.real_costs = data.real_costs;

      // Nur carpool hinzufügen, wenn es gültige Werte enthält
      if (data.carpool !== undefined) {
        if (Array.isArray(data.carpool) && data.carpool.length > 0) {
          const validCarpool = data.carpool.filter((id: any) => id && id !== null && id !== undefined);
          if (validCarpool.length > 0) {
            baserowData.carpool = validCarpool;
          } else {
            baserowData.carpool = []; // Leeres Array um alle Relationen zu löschen
          }
        } else {
          baserowData.carpool = []; // Leeres Array um alle Relationen zu löschen
        }
      }

      // Nur documents hinzufügen, wenn es gültige Werte enthält
      if (data.documents !== undefined) {
        if (Array.isArray(data.documents) && data.documents.length > 0) {
          const validDocuments = data.documents.filter((id: any) => id && id !== null && id !== undefined);
          if (validDocuments.length > 0) {
            baserowData.documents = validDocuments;
          } else {
            baserowData.documents = []; // Leeres Array um alle Relationen zu löschen
          }
        } else {
          baserowData.documents = []; // Leeres Array um alle Relationen zu löschen
        }
      }

      console.log('Updating damage report data:', baserowData);

      const response = await axios.patch(`${DAMAGE_REPORTS_API_URL}${id}/?user_field_names=true`, baserowData, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Aktualisieren der Schadensmeldung:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Aktualisieren der Schadensmeldung:', error);
      throw new Error('Fehler beim Aktualisieren der Schadensmeldung');
    }
  }

  async deleteDamageReport(id: number): Promise<void> {
    try {
      await axios.delete(`${DAMAGE_REPORTS_API_URL}${id}/`, {
        headers: this.getHeaders(),
      });
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Löschen des Schadensberichts:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Löschen des Schadensberichts:', error);
      throw new Error('Fehler beim Löschen des Schadensberichts');
    }
  }

  // Neue Methode: Fahrzeug einem Benutzer zuweisen
  async assignVehicleToUser(vehicleId: number, userId: number): Promise<any> {
    try {
      // Sende nur die Benutzer-ID (Baserow erwartet ein Array von IDs)
      const response = await axios.patch(`${VEHICLES_API_URL}${vehicleId}/?user_field_names=true`, {
        user: [userId]
      }, {
        headers: this.getHeaders(),
      });
      
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Zuweisen des Fahrzeugs:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Zuweisen des Fahrzeugs:', error);
      throw new Error('Fehler beim Zuweisen des Fahrzeugs');
    }
  }

  // Neue Methode: Benutzerzuordnung von Fahrzeug entfernen
  async removeVehicleAssignment(vehicleId: number): Promise<any> {
    try {
      // Setze user-Feld auf leeres Array
      const response = await axios.patch(`${VEHICLES_API_URL}${vehicleId}/?user_field_names=true`, {
        user: []
      }, {
        headers: this.getHeaders(),
      });
      
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        console.error('Fehler beim Entfernen der Fahrzeugzuordnung:', apiError);
        throw new Error(apiError.detail || JSON.stringify(apiError));
      }
      console.error('Fehler beim Entfernen der Fahrzeugzuordnung:', error);
      throw new Error('Fehler beim Entfernen der Fahrzeugzuordnung');
    }
  }

  // Hilfsmethoden für Status-Konvertierung
  getStatusValue(statusId: number): string {
    const statusMap: Record<number, string> = {
      5382: 'Gemeldet',
      5383: 'In Bearbeitung',
      5384: 'Erledigt'
    };
    return statusMap[statusId] || 'Unbekannt';
  }

  getStatusId(statusValue: string): number {
    const statusMap: Record<string, number> = {
      'Gemeldet': 5382,
      'In Bearbeitung': 5383,
      'Erledigt': 5384
    };
    return statusMap[statusValue] || 5382;
  }

  // Versteuerungs-Hilfsmethoden
  getTaxationValue(taxation: any): string {
    // Wenn taxation ein Objekt ist (wie bei Baserow Select-Feldern), extrahiere den value
    if (typeof taxation === 'object' && taxation !== null) {
      if (taxation.value) {
        return taxation.value;
      }
      // Fallback: versuche mit ID zu mappen
      const taxationId = taxation.id;
      if (taxationId) {
        switch (taxationId) {
          case TAXATION_OPTIONS.COMBUSTION: 
            return '1% Verbrenner';
          case TAXATION_OPTIONS.HYBRID: 
            return '0,5% Hybrid';
          case TAXATION_OPTIONS.ELECTRIC: 
            return '0,25% Elektro';
          case TAXATION_OPTIONS.NONE:
            return 'Keine Versteuerung';
          default: 
            return 'Keine Versteuerung';
        }
      }
    }
    
    // Wenn taxation eine Nummer ist
    if (typeof taxation === 'number') {
      switch (taxation) {
        case TAXATION_OPTIONS.COMBUSTION: 
          return '1% Verbrenner';
        case TAXATION_OPTIONS.HYBRID: 
          return '0,5% Hybrid';
        case TAXATION_OPTIONS.ELECTRIC: 
          return '0,25% Elektro';
        case TAXATION_OPTIONS.NONE:
          return 'Keine Versteuerung';
        default: 
          return 'Keine Versteuerung';
      }
    }
    
    // Wenn taxation bereits ein String ist
    if (typeof taxation === 'string') {
      return taxation;
    }
    
    return 'Keine Versteuerung';
  }

  getTaxationId(taxationValue: string): number {
    switch (taxationValue) {
      case '1% Verbrenner': return TAXATION_OPTIONS.COMBUSTION;
      case '0,5% Hybrid': return TAXATION_OPTIONS.HYBRID;
      case '0,25% Elektro': return TAXATION_OPTIONS.ELECTRIC;
      case 'Keine Versteuerung':
      default: return TAXATION_OPTIONS.NONE;
    }
  }

  // Führerscheinkontrolle Status-Methoden
  getLicenseStatusValue(statusId: number): string {
    switch (statusId) {
      case LICENSE_STATUS_OPTIONS.VALID:
        return 'valid';
      case LICENSE_STATUS_OPTIONS.EXPIRING_SOON:
        return 'expiring_soon';
      case LICENSE_STATUS_OPTIONS.EXPIRED:
        return 'expired';
      default:
        return 'valid';
    }
  }

  getLicenseStatusId(statusValue: string): number {
    switch (statusValue) {
      case 'valid':
        return LICENSE_STATUS_OPTIONS.VALID;
      case 'expiring_soon':
        return LICENSE_STATUS_OPTIONS.EXPIRING_SOON;
      case 'expired':
        return LICENSE_STATUS_OPTIONS.EXPIRED;
      default:
        return LICENSE_STATUS_OPTIONS.VALID;
    }
  }

  // Führerscheinkontrolle-API-Methoden
  async createLicenseCheck(data: {
    title: string;
    user_id: number;
    check_date: string;
    is_checked: boolean;
    uploaded_file?: File;
    notes?: string;
  }): Promise<any> {
    try {
      // Wenn eine Datei hochgeladen wurde, lade sie zuerst hoch und erstelle ein Dokument
      let documentId = null;
      if (data.uploaded_file) {
        const uploadResult = await this.uploadFile(data.uploaded_file);
        
        // Erstelle ein Dokument in der Documents-Tabelle für das Führerscheinbild
        const documentData = {
          title: getFileNameWithoutExtension(data.uploaded_file.name),
          description: `Führerscheinbild für Kontrolle am ${data.check_date}`,
          files: [uploadResult.name],
          active: true,
          content_type: getMimeType(data.uploaded_file.name),
          type: 2688, // Standard-Dokumenttyp für Führerscheinbilder
          isLicenseDocument: true // Flag für Führerschein-Dokumente
        };
        
        const documentResponse = await this.createDocument(documentData);
        documentId = documentResponse.id;
      }

      const licenseCheckData = {
        [LICENSE_CHECK_FIELDS.TITLE]: data.title,
        [LICENSE_CHECK_FIELDS.USER]: [data.user_id], // Array für Link-Feld
        [LICENSE_CHECK_FIELDS.CHECK_DATE]: data.check_date,
        [LICENSE_CHECK_FIELDS.IS_CHECKED]: data.is_checked,
        [LICENSE_CHECK_FIELDS.NOTES]: data.notes || ''
      };

      // Dokument-ID hinzufügen, falls vorhanden
      if (documentId) {
        licenseCheckData[LICENSE_CHECK_FIELDS.UPLOADED_FILE] = [documentId]; // Array für Link-Feld
      }

      console.log('Erstelle Führerscheinkontrolle mit Daten:', licenseCheckData);

      const response = await axios.post(LICENSE_CHECKS_API_URL, licenseCheckData, {
        headers: this.getHeaders(),
      });

      console.log('Führerscheinkontrolle erstellt:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Erstellen der Führerscheinkontrolle:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Erstellen der Führerscheinkontrolle');
    }
  }

  async getLicenseChecks(userId?: number): Promise<any> {
    try {
      let url = LICENSE_CHECKS_API_URL;
      
      // Filter nach Benutzer-ID, falls angegeben
      // Hinweis: Link-Felder können nicht mit equal gefiltert werden
      // Daher laden wir alle Kontrollen und filtern client-seitig
      if (userId) {
        console.log('Hinweis: Filterung nach Benutzer-ID wird client-seitig durchgeführt');
      }

      const response = await axios.get(url, {
        headers: this.getHeaders(),
      });

      // Transformiere die Baserow-Antwort in unser Format
      const transformedData = await Promise.all(response.data.results.map(async (item: any) => {
        // Berechne Status basierend auf check_date (6 Monate)
        const checkDate = new Date(item[LICENSE_CHECK_FIELDS.CHECK_DATE]);
        const expiresAt = new Date(checkDate.getTime() + 6 * 30 * 24 * 60 * 60 * 1000);
        const now = new Date();
        
        let status = 'valid';
        if (expiresAt < now) {
          status = 'expired';
        } else if (expiresAt.getTime() - now.getTime() < 30 * 24 * 60 * 60 * 1000) { // 30 Tage
          status = 'expiring_soon';
        }

        // Lade Dokument-Informationen, falls vorhanden
        let uploadedFile = null;
        if (item[LICENSE_CHECK_FIELDS.UPLOADED_FILE]?.[0]) {
          try {
            const documentId = item[LICENSE_CHECK_FIELDS.UPLOADED_FILE][0].id;
            const documentResponse = await this.getDocument(documentId);
            
            console.log('Geladenes Dokument:', documentResponse);
            
            // Baserow verwendet eine spezielle URL-Struktur für Dateien
            // Versuche die Datei direkt über die Baserow File-API zu laden
            let fileUrl = null;
            
            // Prüfe verschiedene mögliche URL-Properties
            if (documentResponse.file) {
              fileUrl = documentResponse.file;
            } else if (documentResponse.file_url) {
              fileUrl = documentResponse.file_url;
            } else if (documentResponse.url) {
              fileUrl = documentResponse.url;
            } else if (documentResponse.download_url) {
              fileUrl = documentResponse.download_url;
            } else if (documentResponse.id) {
              // Fallback: Konstruiere URL basierend auf Dokument-ID
              // Baserow verwendet: /api/user-files/upload-file/{file_id}/
              fileUrl = `${BASEROW_CONFIG.BASE_URL}/user-files/upload-file/${documentResponse.id}/`;
            }
            
            // Versuche die Datei direkt über die API zu laden
            if (documentResponse.id) {
              try {
                const fileBlob = await this.downloadFileById(documentResponse.id);
                const fileUrl = URL.createObjectURL(fileBlob);
                uploadedFile = {
                  name: documentResponse.name || documentResponse.title || 'Dokument',
                  url: fileUrl
                };
              } catch (downloadError) {
                console.warn('Fehler beim Laden der Datei über API:', downloadError);
                // Fallback zur URL-Konstruktion
                uploadedFile = {
                  name: documentResponse.name || documentResponse.title || 'Dokument',
                  url: `${BASEROW_CONFIG.BASE_URL}/user-files/upload-file/${documentResponse.id}/`
                };
              }
            }
            
            if (fileUrl) {
              uploadedFile = {
                name: documentResponse.name || documentResponse.title || 'Dokument',
                url: fileUrl
              };
            } else {
              console.warn('Dokument hat keine erkennbare file-URL:', documentResponse);
              uploadedFile = {
                name: documentResponse.name || documentResponse.title || 'Dokument',
                url: '#'
              };
            }
          } catch (error) {
            console.error('Fehler beim Laden des Dokuments:', error);
            uploadedFile = {
              name: item[LICENSE_CHECK_FIELDS.UPLOADED_FILE][0].value || 'Dokument',
              url: '#'
            };
          }
        }

        return {
          id: item.id,
          title: item[LICENSE_CHECK_FIELDS.TITLE],
          user_id: item[LICENSE_CHECK_FIELDS.USER]?.[0]?.id || 0,
          user_name: item[LICENSE_CHECK_FIELDS.USER]?.[0]?.value || `Benutzer ${item[LICENSE_CHECK_FIELDS.USER]?.[0]?.id || 0}`,
          check_date: item[LICENSE_CHECK_FIELDS.CHECK_DATE],
          is_checked: item[LICENSE_CHECK_FIELDS.IS_CHECKED],
          uploaded_file: uploadedFile,
          notes: item[LICENSE_CHECK_FIELDS.NOTES],
          status: status,
          expires_at: expiresAt.toISOString().split('T')[0],
          created_at: item.created_at,
          updated_at: item.updated_at
        };
      }));

      return {
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        results: transformedData
      };
    } catch (error: any) {
      console.error('Fehler beim Laden der Führerscheinkontrollen:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Laden der Führerscheinkontrollen');
    }
  }

  async updateLicenseCheck(id: number, data: any): Promise<any> {
    try {
      // Wenn eine Datei hochgeladen wurde, lade sie zuerst hoch und erstelle ein Dokument
      let documentId = null;
      if (data.uploaded_file) {
        const uploadResult = await this.uploadFile(data.uploaded_file);
        
        // Erstelle ein Dokument in der Documents-Tabelle
        const documentData = {
          name: data.uploaded_file.name,
          file: uploadResult.url
          // type-Feld entfernt, da es Validierungsfehler verursacht
        };
        
        const documentResponse = await this.createDocument(documentData);
        documentId = documentResponse.id;
      }
      
      const updateData: any = {};
      
      // Nur geänderte Felder hinzufügen
      if (data.title !== undefined) {
        updateData[LICENSE_CHECK_FIELDS.TITLE] = data.title;
      }
      if (data.user_id !== undefined) {
        updateData[LICENSE_CHECK_FIELDS.USER] = [data.user_id]; // Array für Link-Feld
      }
      if (data.check_date !== undefined) {
        updateData[LICENSE_CHECK_FIELDS.CHECK_DATE] = data.check_date;
      }
      if (data.is_checked !== undefined) {
        updateData[LICENSE_CHECK_FIELDS.IS_CHECKED] = data.is_checked;
      }
      if (data.notes !== undefined) {
        updateData[LICENSE_CHECK_FIELDS.NOTES] = data.notes;
      }
      if (documentId) {
        updateData[LICENSE_CHECK_FIELDS.UPLOADED_FILE] = [documentId]; // Array für Link-Feld
      }

      const response = await axios.patch(`${LICENSE_CHECKS_API_URL}${id}/`, updateData, {
        headers: this.getHeaders(),
      });

      console.log('Führerscheinkontrolle aktualisiert:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Aktualisieren der Führerscheinkontrolle:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Aktualisieren der Führerscheinkontrolle');
    }
  }

  async deleteLicenseCheck(id: number): Promise<void> {
    try {
      await axios.delete(`${LICENSE_CHECKS_API_URL}${id}/`, {
        headers: this.getHeaders(),
      });
      
      console.log('Führerscheinkontrolle gelöscht:', id);
    } catch (error: any) {
      console.error('Fehler beim Löschen der Führerscheinkontrolle:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Löschen der Führerscheinkontrolle');
    }
  }



  // Benutzer-Management API-Methoden
  async getUsers(): Promise<any> {
    try {
      const response = await axios.get(USERS_API_URL, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Laden der Benutzer:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Laden der Benutzer');
    }
  }

  async getUser(id: number): Promise<any> {
    try {
      const response = await axios.get(`${USERS_API_URL}${id}/`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Laden des Benutzers:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Laden des Benutzers');
    }
  }

  async createUser(data: {
    name: string;
    email?: string;
    role?: string;
    notes?: string;
  }): Promise<any> {
    try {
      const userData = {
        name: data.name,
        email: data.email || '',
        role: data.role || 'Fahrer',
        notes: data.notes || ''
      };

      const response = await axios.post(USERS_API_URL, userData, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Erstellen des Benutzers:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Erstellen des Benutzers');
    }
  }

  async updateUser(id: number, data: any): Promise<any> {
    try {
      const response = await axios.patch(`${USERS_API_URL}${id}/`, data, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Aktualisieren des Benutzers:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Aktualisieren des Benutzers');
    }
  }

  async deleteUser(id: number): Promise<void> {
    try {
      const response = await axios.delete(`${USERS_API_URL}${id}/`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Fehler beim Löschen des Benutzers:', error);
      if (error.response?.data) {
        throw new Error(`API Fehler: ${JSON.stringify(error.response.data)}`);
      }
      throw new Error('Fehler beim Löschen des Benutzers');
    }
  }

  // Spezielle Suchfunktion für Tayron_final
  async searchForTayronFinal(): Promise<any> {
    try {
      console.log('=== SUCHEN NACH TAYRON_FINAL ===');
      
      // 1. Suche in der documents Tabelle
      const documentsResponse = await axios.get(`${DOCUMENTS_API_URL}?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      console.log('Documents table response:', documentsResponse.data);
      
      // Suche nach Tayron_final in den Dokumenten
      const tayronDocuments = documentsResponse.data.results.filter((doc: any) => 
        doc.name && doc.name.toLowerCase().includes('tayron')
      );
      
      console.log('Found Tayron documents in documents table:', tayronDocuments);
      
      // 2. Suche in der vehicles Tabelle (falls dort gespeichert)
      const vehiclesResponse = await axios.get(`${VEHICLES_API_URL}?user_field_names=true`, {
        headers: this.getHeaders(),
      });
      
      console.log('Vehicles table response:', vehiclesResponse.data);
      
      // Suche nach Tayron_final in den Fahrzeug-Dokumenten
      const vehiclesWithTayron = vehiclesResponse.data.results.filter((vehicle: any) => 
        vehicle.documents && Array.isArray(vehicle.documents) && 
        vehicle.documents.some((doc: string) => doc.toLowerCase().includes('tayron'))
      );
      
      console.log('Found vehicles with Tayron documents:', vehiclesWithTayron);
      
      return {
        documentsTable: tayronDocuments,
        vehiclesTable: vehiclesWithTayron
      };
    } catch (error: any) {
      console.error('Fehler beim Suchen nach Tayron_final:', error);
      throw new Error('Fehler beim Suchen nach Tayron_final');
    }
  }
}

const baserowApiInstance = new BaserowApi();

export default baserowApiInstance;